/* ─────────────────────────────────────────────────────────────
   STOCK SNAPSHOT — top of the Summary tab
   GET /api/v1/stocks/{ticker}/summary

     intrinsic_value      our DCF per share, upside vs price, confidence
     analyst_target       street target, upside, consensus, analyst counts (3m / 12m)
     fa_grade             grade, rating, score
     news_sentiment       weekly press score, band, week-over-week change
     earnings_sentiment   last call's management tone, band, quarter
     implied_volatility   weekly + monthly ATM straddle: ±move %, price band, ATM IV

   Percentages arrive as percentages — never multiplied. Each tile opens the tab
   that explains it. Null rule: hide what is missing, never show "N/A".
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getStockSummary } from "../../../api/stocks_api";
import { formatCurrency } from "../../../utils/currency";

const pct = (v, d = 1, signed = true) =>
  v == null || Number.isNaN(Number(v))
    ? null
    : `${signed && Number(v) > 0 ? "+" : ""}${Number(v).toFixed(d)}%`;

const tone = (v, pos = 0, neg = 0) =>
  v == null ? "var(--color-text-primary)" : Number(v) > pos ? "var(--color-success)" : Number(v) < neg ? "var(--color-error)" : "var(--color-text-primary)";

const bandTone = (score) =>
  score == null ? "var(--color-text-primary)" : score >= 55 ? "var(--color-success)" : score < 45 ? "var(--color-error)" : "var(--color-warning)";

const fmtDay = (s) => {
  if (!s) return null;
  const d = new Date(`${String(s).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

/* Q3 FY2026 → "Q2 FY2026"; Q1 FY2026 → "Q4 FY2025" */
const prevQuarter = (fy, fq) => {
  if (fy == null || fq == null) return "the previous call";
  const q = Number(fq), y = Number(fy);
  return q > 1 ? `Q${q - 1} FY${y}` : `Q4 FY${y - 1}`;
};

const Tile = ({ label, value, valueColor, sub, sub2, onClick, title }) => (
  <div
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick()) : undefined}
    title={title}
    style={{
      padding: "14px 16px",
      borderRadius: "var(--radius-lg)",
      background: "linear-gradient(135deg, var(--color-surface) 0%, var(--color-bg-secondary) 100%)",
      border: "1px solid var(--color-border-primary)",
      cursor: onClick ? "pointer" : "default",
      minWidth: 0,
      transition: "var(--transition-normal)",
    }}
  >
    <div style={{ fontSize: "var(--font-size-xs)", fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
      {label}
    </div>
    <div style={{ fontSize: "var(--font-size-xl)", fontWeight: 800, color: valueColor ?? "var(--color-text-primary)", fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>
      {value}
    </div>
    {sub ? <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", marginTop: 4 }}>{sub}</div> : null}
    {sub2 ? <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-muted)", marginTop: 2 }}>{sub2}</div> : null}
  </div>
);

export const StockSnapshot = ({ ticker }) => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setD(null);
    (async () => {
      const res = await getStockSummary(ticker);
      if (!cancelled && res?.success) setD(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  if (!d) return null;

  const go = (slug) => () => navigate(`/stocks/${ticker}/details/${slug}`);
  const iv = d.intrinsic_value ?? {};
  const at = d.analyst_target ?? {};
  const fa = d.fa_grade ?? {};
  const ns = d.news_sentiment ?? {};
  const es = d.earnings_sentiment ?? {};
  const vol = d.implied_volatility ?? {};
  const wk = vol.weekly ?? {};
  const mo = vol.monthly ?? {};

  const tiles = [
    iv.iv_per_share != null && !iv.declined_reason
      ? {
          label: "True value (our DCF)",
          value: formatCurrency(iv.iv_per_share),
          sub: <span style={{ color: tone(iv.upside_downside_pct) }}>{pct(iv.upside_downside_pct)} vs price</span>,
          sub2: [iv.confidence ? `${String(iv.confidence).toLowerCase()} confidence` : null, iv.as_of_date ? `as of ${fmtDay(iv.as_of_date)}` : null].filter(Boolean).join(" · "),
          onClick: go("true-value"),
        }
      : null,
    at.target_price != null
      ? {
          label: "Analyst target",
          value: formatCurrency(at.target_price),
          sub: (
            <>
              <span style={{ color: tone(at.upside_pct) }}>{pct(at.upside_pct)}</span>
              {at.consensus ? ` · ${at.consensus}` : ""}
            </>
          ),
          sub2: at.num_analysts_3m != null || at.num_analysts_12m != null ? `${at.num_analysts_3m ?? "—"} / ${at.num_analysts_12m ?? "—"} analysts (3m / 12m)` : null,
          onClick: go("fairvalue"),
        }
      : null,
    fa.grade
      ? {
          label: "FA grade",
          value: `${fa.grade}${fa.rating ? ` · ${fa.rating}` : ""}`,
          valueColor: /buy/i.test(fa.rating ?? "") ? "var(--color-success)" : /sell/i.test(fa.rating ?? "") ? "var(--color-error)" : undefined,
          sub: fa.score != null ? `score ${Number(fa.score).toFixed(1)}` : null,
          sub2: fa.rating_date ? `rated ${fmtDay(fa.rating_date)}` : null,
          onClick: go("ratings"),
        }
      : null,
    ns.composite_score != null && !ns.quiet
      ? {
          label: "News sentiment",
          value: Number(ns.composite_score).toFixed(1),
          valueColor: bandTone(Number(ns.composite_score)),
          sub: (
            <>
              {ns.band}
              {ns.wow_delta != null ? <span style={{ color: tone(ns.wow_delta) }}> · {pct(ns.wow_delta, 1).replace("%", "")} w/w</span> : null}
            </>
          ),
          sub2: ns.week_end ? `week to ${fmtDay(ns.week_end)}` : ns.iso_week,
          onClick: go("news-sentiment"),
        }
      : null,
    es.composite_score != null
      ? {
          label: "Earnings-call sentiment",
          value: Number(es.composite_score).toFixed(1),
          valueColor: bandTone(Number(es.composite_score)),
          /* qoq_delta: change vs the previous quarter's call; NULL when the prior call is
             missing or was scored under a different rubric version — say so, never a bare dash. */
          sub: (
            <>
              {es.band}
              {es.qoq_delta != null ? (
                <span style={{ color: tone(es.qoq_delta) }}>
                  {" "}· {pct(es.qoq_delta, 1).replace("%", "")} vs {prevQuarter(es.fiscal_year, es.fiscal_quarter)}
                </span>
              ) : (
                <span style={{ color: "var(--color-text-muted)" }}> · no comparable prior call</span>
              )}
            </>
          ),
          sub2: es.fiscal_quarter != null ? `Q${es.fiscal_quarter} FY${es.fiscal_year}${es.transcript_date ? ` · call ${fmtDay(es.transcript_date)}` : ""}` : null,
          onClick: go("earnings-review"),
        }
      : null,
    wk.implied_move != null || mo.implied_move != null
      ? {
          label: "Implied move",
          value: [wk.implied_move != null ? `±${Number(wk.implied_move).toFixed(1)}% wk` : null, mo.implied_move != null ? `±${Number(mo.implied_move).toFixed(1)}% mo` : null]
            .filter(Boolean)
            .join(" · "),
          sub: wk.band_low != null ? `${formatCurrency(wk.band_low)} – ${formatCurrency(wk.band_high)} by ${fmtDay(wk.expiry)}` : null,
          sub2: mo.band_low != null ? `${formatCurrency(mo.band_low)} – ${formatCurrency(mo.band_high)} by ${fmtDay(mo.expiry)}${mo.atm_iv != null ? ` · ATM IV ${Number(mo.atm_iv).toFixed(1)}%` : ""}` : null,
          title: "What the at-the-money straddle prices in either direction by each expiry",
          onClick: go("implied-volatility"),
        }
      : null,
  ].filter(Boolean);

  if (tiles.length === 0) return null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--font-size-lg)", fontWeight: 700, color: "var(--color-text-primary)" }}>Snapshot</span>
        <span style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-muted)" }}>
          {d.current_price != null ? `price ${formatCurrency(d.current_price)}` : ""}
          {d.price_as_of ? ` · ${fmtDay(d.price_as_of)}` : ""} · click a tile for the detail
        </span>
      </div>
      <div style={{ display: "grid", /* three per row; drops to two/one on narrow screens */
                    gridTemplateColumns: "repeat(auto-fit, minmax(max(220px, calc((100% - 24px) / 3)), 1fr))", gap: 12 }}>
        {tiles.map((t) => (
          <Tile key={t.label} {...t} />
        ))}
      </div>
    </div>
  );
};

export default StockSnapshot;
